/**
 * The sidebar's "needs your attention" badge: active alerts + open incidents.
 *
 * Both halves live here rather than side by side in `__root.tsx` because they
 * share one audience rule and one feature flag, and being written twice is how
 * they drifted: the audience condition was added to the alert count and not to
 * the incident list, so an `agent` session — which is not an alert audience —
 * stopped calling `GET /alerts` and went on calling `GET /api/v1/incidents` on
 * every mount, collecting a 403 the badge could do nothing with.
 *
 * The rule the API enforces is `PERMISSION_MATRIX.alert.read`, read through
 * `alertAudienceScope`. An owner is always an audience; anyone else is one only
 * if the matrix says so. A refusal is not a badge state — it is noise — so
 * neither request is made when the answer is already known.
 */

import { useQuery } from '@tanstack/react-query';
import { alertAudienceScope } from '@aica/core';
import type { GlobalRole } from '@aica/core/rbac';
import { alertsActiveCountOptions } from '@/lib/query-options';
import type { ApiClient } from '@/lib/api';

export interface AlertsBadgeInput {
  apiClient: ApiClient;
  selectedOrgId: string | null;
  /**
   * Everything unrelated to permissions that must be true first: after first
   * paint, signed in, an organization selected, the role resolved.
   */
  ready: boolean;
  globalRole: GlobalRole | null;
  isOwner: boolean;
  /** `enableAlerts` — with the feature off, neither surface exists. */
  alertsEnabled: boolean;
}

/**
 * May this session read alerts at all — the same question the API answers with
 * 403. Exported so the decision can be asserted directly; both queries below
 * are gated on it, and adding a third one must reuse it.
 */
export function canReadAlertsBadge(globalRole: GlobalRole | null, isOwner: boolean) {
  return isOwner || alertAudienceScope(globalRole ?? null) !== 'none';
}

export function useAlertsBadgeCount({
  apiClient,
  selectedOrgId,
  ready,
  globalRole,
  isOwner,
  alertsEnabled,
}: AlertsBadgeInput): number {
  const enabled = ready && alertsEnabled && canReadAlertsBadge(globalRole, isOwner);

  const { data: activeAlertCount } = useQuery({
    ...alertsActiveCountOptions(apiClient, selectedOrgId),
    enabled,
  });

  const { data: incidentsData } = useQuery({
    queryKey: ['incidents', selectedOrgId],
    queryFn: () => apiClient.incidents.list(),
    enabled,
  });

  // Items that still need user attention. Acknowledged alerts and
  // in-progress/closed incidents are excluded — the user has already engaged
  // with them and the badge would just nag.
  return (
    (activeAlertCount ?? 0) +
    (incidentsData?.incidents?.filter((i: { status: string }) => i.status === 'open')
      .length ?? 0)
  );
}
